import { Distribute, IconButton, SvgArrowBack, Text } from '@loomhq/lens';
import classnames from 'classnames';
import React from 'react';
import ResizeObserver from 'resize-observer-polyfill';

import { EventsList, ViewerAvatar, ViewerName, ViewsList } from './components';
import { percentToColor } from './utils';

import styles from './styles.module.less';

const Details = ({ viewer, onBack }) => {
  const ref = React.useRef();
  const {
    user = {},
    views = [],
    percentCompleted = 0,
    ctaClicks,
    events = [],
  } = viewer;

  // scroll the parent on mount and on resize
  // to slide to this element
  // scrollIntoView will also scroll vertically
  // ending up hidden by the header :/
  React.useEffect(() => {
    const el = ref?.current;

    document.documentElement.scrollTo({ top: 0 });
    el?.parentElement?.scrollTo({ left: el.offsetWidth, behavior: 'smooth' });
    // shrink after scrolling animation to avoid jank
    setTimeout(() => el?.parentElement.classList.add(styles.shrinked), 250);

    const ro = new ResizeObserver(([el]) => {
      el?.target?.parentElement?.scrollTo({
        left: el?.contentRect?.width,
        behavior: 'smooth',
      });
    });

    ro.observe(el);

    return () => ro.disconnect();
  }, []);

  return (
    <section className={styles.slideable} ref={ref}>
      <Distribute
        htmlTag="header"
        alignment="center"
        isSpread
        className={styles.detailsHeader}
      >
        <Distribute alignment="center" isSpread onClick={onBack}>
          <IconButton icon={<SvgArrowBack />} />
          <ViewerName name={user.name} />
        </Distribute>
        <ViewerAvatar
          avatar={user.avatar}
          size="medium"
          color={user.color}
          variant={user.variant}
        />
      </Distribute>
      <div className={styles.detailsBox}>
        <div className={styles.detailsBoxSmall}>
          <Text fontWeight="semiBold" className={styles.bigNum} color="blue">
            {views.length}
          </Text>
          <Text fontWeight="semiBold">Total video views</Text>
        </div>
        <div className={styles.detailsBoxSmall}>
          <DonutProgress percent={percentCompleted} />
          <Text fontWeight="semiBold">Completion rate</Text>
        </div>
        <div>
          <Text fontWeight="semiBold">View Times</Text>
          <ViewsList views={views} />
        </div>
        {Boolean(ctaClicks) && (
          <Distribute alignment="center" gap={2}>
            <CtaSvg />
            <Text fontWeight="semiBold">
              {ctaClicks} Call to Action Button Clicks
            </Text>
          </Distribute>
        )}
      </div>
      <EventsList events={events} />
    </section>
  );
};

export default Details;

const DonutProgress = ({ percent }) => {
  const dashArray = [percent, 100 - percent].join(' ');
  const color = percentToColor(percent);

  return (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 40 40"
      className={styles.donut}
    >
      <circle
        className={styles.donutRing}
        cx="20"
        cy="20"
        r="16"
        fill="transparent"
        strokeWidth="1"
      ></circle>

      {percent && (
        <circle
          className={classnames(styles.donutSegment, styles[color])}
          cx="20"
          cy="20"
          r="16"
          fill="transparent"
          strokeWidth="1"
          strokeDasharray={dashArray}
          strokeDashoffset="25"
        ></circle>
      )}
      <text
        x="50%"
        y="52%"
        className={classnames(styles.donutNumber, styles[color])}
        dominantBaseline="middle"
        textAnchor="middle"
      >
        {percent != null ? `${percent}%` : '--'}
      </text>
    </svg>
  );
};

const CtaSvg = () => (
  <svg
    width="40"
    height="40"
    viewBox="0 0 40 40"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clipPath="url(#clip0)">
      <path
        d="M28.9001 30.4999H10.9001C5.7001 30.4999 1.6001 26.2999 1.6001 21.1999C1.6001 15.9999 5.8001 11.8999 10.9001 11.8999H28.9001C34.1001 11.8999 38.2001 16.0999 38.2001 21.1999C38.2001 26.2999 34.0001 30.4999 28.9001 30.4999Z"
        fill="url(#paint0_linear)"
      />
      <path
        d="M25 22.7H14.7C13.9 22.7 13.2 22 13.2 21.2C13.2 20.4 13.9 19.7 14.7 19.7H25C25.8 19.7 26.5 20.4 26.5 21.2C26.5 22 25.9 22.7 25 22.7Z"
        fill="white"
      />
      <path d="M27 25.3L28.1 37.7001L38.1 31.4L27 25.3Z" fill="white" />
      <path
        d="M38.8 30.1L27.7 24C27.2 23.7 26.6 23.8 26.2 24.1C25.7 24.4 25.5 24.9 25.5 25.5L26.7 37.9C26.7 38.4 27.1 38.9 27.5 39.1C27.7 39.2 27.9 39.3 28.2 39.3C28.5 39.3 28.8 39.2 29 39.1L31.8 37.3L32.9 39C33.4 39.8 34.4 40 35.1 39.5L36.6 38.5C37.4 38 37.6 37 37.1 36.3L36 34.5L38.9 32.7C39.4 32.4 39.6 31.9 39.6 31.4C39.6 30.9 39.3 30.4 38.8 30.1ZM28.7 28L35.1 31.6L29.4 35.2L28.7 28Z"
        fill="url(#paint1_linear)"
      />
      <path
        d="M3.2 11.2999C4.96731 11.2999 6.4 9.86721 6.4 8.0999C6.4 6.33259 4.96731 4.8999 3.2 4.8999C1.43269 4.8999 0 6.33259 0 8.0999C0 9.86721 1.43269 11.2999 3.2 11.2999Z"
        fill="#FD6860"
      />
      <path
        d="M8.7 3.4C9.63888 3.4 10.4 2.63888 10.4 1.7C10.4 0.761116 9.63888 0 8.7 0C7.76112 0 7 0.761116 7 1.7C7 2.63888 7.76112 3.4 8.7 3.4Z"
        fill="#FD6860"
      />
    </g>
    <defs>
      <linearGradient
        id="paint0_linear"
        x1="19.8836"
        y1="12.6944"
        x2="19.8836"
        y2="30.9873"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#19AEFF" />
        <stop offset="1" stopColor="#516BF0" />
      </linearGradient>
      <linearGradient
        id="paint1_linear"
        x1="25.4578"
        y1="31.7025"
        x2="39.5774"
        y2="31.7025"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#FE7A60" />
        <stop offset="1" stopColor="#FD5F60" />
      </linearGradient>
      <clipPath id="clip0">
        <rect width="39.6" height="39.6" fill="white" />
      </clipPath>
    </defs>
  </svg>
);
